import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { client } from "../Clients";
import type { LogEntry } from "../API/Models";

interface UseLogsDataOptions {
	limit: number;
	includeDebug?: boolean;
	tags?: string[];
}

export const useLogsData = ({ limit, includeDebug = true, tags }: UseLogsDataOptions) => {
	const [logs, setLogs] = useState<LogEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastUpdated, setLastUpdated] = useState<string | null>(null);
	const isMountedRef = useRef(true);

	const tagQuery = useMemo(() => {
		if (!tags || tags.length === 0) {
			return undefined;
		}
		return tags.join(",");
	}, [tags]);

	const fetchLogs = useCallback(
		async (manual = false) => {
			if (manual) {
				setIsRefreshing(true);
			} else {
				setLoading(true);
			}
			setError(null);
			try {
				const response = await client.logs.apiLogsGet(limit, includeDebug, tagQuery);
				if (!isMountedRef.current) return;
				setLogs(response.data ?? []);
				setLastUpdated(new Date().toISOString());
			} catch (caughtError) {
				if (!isMountedRef.current) return;
				console.error(caughtError);
				setError("Unable to load logs. Please try again.");
			} finally {
				if (!isMountedRef.current) return;
				if (manual) {
					setIsRefreshing(false);
				} else {
					setLoading(false);
				}
			}
		},
		[includeDebug, limit, tagQuery]
	);

	useEffect(() => {
		isMountedRef.current = true;
		fetchLogs();
		return () => {
			isMountedRef.current = false;
		};
	}, [fetchLogs]);

	return {
		logs,
		loading,
		error,
		lastUpdated,
		isRefreshing,
		refresh: () => fetchLogs(true)
	};
};

export default useLogsData;

